import { z } from "zod";
import { TASK_NAMES } from "./workflows/tasks";

export const TASK_QUEUE = "gtm.workflows";

/**
 * One entry per provider. The provider prefix of a workflow key is the only
 * routing key the system needs, so adding a provider is an entry here rather
 * than a new branch in every lookup.
 */
export const PROVIDERS = {
  github: { kind: "collection", label: "GitHub" },
  reddit: { kind: "collection", label: "Reddit" },
  slack: { kind: "delivery", label: "Slack" },
  webhook: { kind: "delivery", label: "Webhook" },
  posthog: { kind: "analytics", label: "PostHog" },
  anthropic: { kind: "generation", label: "Anthropic" },
} as const;

export type Provider = keyof typeof PROVIDERS;
export type ProviderKind = (typeof PROVIDERS)[Provider]["kind"];

export const isProvider = (value: string): value is Provider => value in PROVIDERS;

/**
 * The workflow catalog. `tasks` is the ordered list a run reports progress
 * against, so the UI can show where a run has got to without the workflow
 * writing to the database. Task names come from `TASK_NAMES` because a module
 * switches on them; spelling one out here is how a task stops running.
 *
 * `intent` says how a workflow may be started:
 *
 * - `sync` — the ordinary kind. Manual or on a schedule, repeatedly.
 * - `backfill` — a bounded, one-off import of history that already happened.
 *   Never scheduled: it has an end, and repeating it on a timer would re-walk
 *   an archive that cannot change to discover nothing.
 * - `delivery` — runs because somebody sent people somewhere.
 * - `enrichment` — an explicit lookup for one person; never scheduled.
 */
export const WORKFLOWS = {
  "github.enrich_person": {
    provider: "github",
    name: "GitHub Email Enrich",
    type: "githubPersonEnrichment",
    intent: "enrichment",
    tasks: [TASK_NAMES.enrichProfiles, "Enrich email from commit patches"],
  },
  "github.sync_repository": {
    provider: "github",
    name: "Sync repository",
    type: "githubRepositorySync",
    intent: "sync",
    tasks: [TASK_NAMES.collectStargazers, TASK_NAMES.collectActivity],
  },
  "github.sync_stargazers": {
    provider: "github",
    name: "Sync stargazers",
    type: "githubStargazersSync",
    intent: "sync",
    tasks: [TASK_NAMES.collectStargazers],
  },
  "github.sync_issues": {
    provider: "github",
    name: "Sync issues",
    type: "githubIssuesSync",
    intent: "sync",
    tasks: [TASK_NAMES.collectIssues],
  },
  "github.sync_activity": {
    provider: "github",
    name: "Sync community activity",
    type: "githubActivitySync",
    intent: "sync",
    tasks: [TASK_NAMES.collectActivity],
  },
  "github.backfill_stars": {
    provider: "github",
    name: "Backfill archived stars",
    type: "githubStarsBackfill",
    intent: "backfill",
    tasks: [TASK_NAMES.backfillArchivedStars],
  },
  "reddit.sync_subreddit": {
    provider: "reddit",
    name: "Sync subreddit",
    type: "redditSubredditSync",
    intent: "sync",
    tasks: [TASK_NAMES.collectRedditPosts, TASK_NAMES.collectRedditComments],
  },
  "slack.send_records": {
    provider: "slack",
    name: "Send to Slack",
    type: "slackSendRecords",
    intent: "delivery",
    tasks: [TASK_NAMES.prepareRecords, TASK_NAMES.deliverRecords],
  },
  "webhook.send_records": {
    provider: "webhook",
    name: "Send to webhook",
    type: "webhookSendRecords",
    intent: "delivery",
    tasks: [TASK_NAMES.prepareRecords, TASK_NAMES.deliverRecords],
  },
} as const;

export type WorkflowIntent = (typeof WORKFLOWS)[keyof typeof WORKFLOWS]["intent"];

export type WorkflowKey = keyof typeof WORKFLOWS;
export const WORKFLOW_KEYS = Object.keys(WORKFLOWS) as WorkflowKey[];

export const WorkflowKeySchema = z.enum(WORKFLOW_KEYS as [WorkflowKey, ...WorkflowKey[]]);

export function workflowProvider(key: string): Provider | null {
  const provider = key.split(".")[0];
  return provider && isProvider(provider) ? provider : null;
}

export function workflowKind(key: string): ProviderKind | null {
  const provider = workflowProvider(key);
  return provider ? PROVIDERS[provider].kind : null;
}

/**
 * Only a sync may be put on a timer. Deliveries run because somebody sent, and
 * a backfill is one-off by definition — scheduling one would re-walk an archive
 * that cannot change, to discover nothing.
 */
export function isSchedulable(key: string): boolean {
  return WORKFLOWS[key as WorkflowKey]?.intent === "sync";
}

/**
 * Why a run started. `backfill` is a third kind rather than a flavour of
 * `manual` because runs are read back by intent: a backfill re-walking history
 * should never be mistaken for ordinary collection falling behind, and the
 * inbox labels what it imports as history rather than news.
 */
export const TriggerSchema = z.enum(["manual", "schedule", "backfill"]);
export type Trigger = z.infer<typeof TriggerSchema>;

/**
 * Every workflow starts from this. The Temporal workflow id is the run's
 * request key, so one workflow id maps to exactly one run and a retry converges
 * on the same ingestion ledger row instead of creating a second.
 */
export const RunRequestSchema = z.object({
  workspaceId: z.string().uuid(),
  workflowKey: WorkflowKeySchema,
  /** A collection source, or a delivery connection. */
  sourceId: z.string().uuid(),
  trigger: TriggerSchema,
  /** The human who asked, when a human asked. Never the writer. */
  requestedBy: z.string().uuid().optional(),
  correlationId: z.string().optional(),
});
export type RunRequest = z.infer<typeof RunRequestSchema>;

/** A delivery carries the people it acts on. We act on people, not signals. */
export const DeliveryRequestSchema = RunRequestSchema.extend({
  personIds: z.array(z.string().uuid()).min(1).max(500),
  signalIds: z.array(z.string().uuid()).min(1).max(500).optional(),
  includeSignalPayload: z.boolean().optional(),
});
export type DeliveryRequest = z.infer<typeof DeliveryRequestSchema>;

/** The schedule id for a source and workflow, and the prefix of its run ids. */
export function scheduleId(workspaceId: string, sourceId: string, workflowKey: string): string {
  return `collection/${workspaceId}/${sourceId}/${workflowKey}`;
}

export function runWorkflowId(
  workspaceId: string,
  sourceId: string,
  workflowKey: string,
  at: Date,
): string {
  const kind = workflowKind(workflowKey) === "delivery" ? "delivery" : "collection";
  return `${kind}/${workspaceId}/${sourceId}/${workflowKey}-${at.toISOString()}`;
}

/** Search attributes, so run listings are a Temporal visibility query. */
export const SEARCH_ATTRIBUTES = {
  workspaceId: "GtmWorkspaceId",
  sourceId: "GtmSourceId",
  workflowKey: "GtmWorkflowKey",
  trigger: "GtmTrigger",
} as const;

/**
 * Progress lives in the workflow's memo, so it is readable without a worker and
 * without a database write.
 */
export interface RunProgress {
  task: string;
  taskIndex: number;
  taskCount: number;
  fetched: number;
  message?: string;
  emailFound?: boolean;
}

export const RunStatusSchema = z.enum([
  "running",
  "completed",
  "failed",
  "cancelled",
  "terminated",
  "timed_out",
  "continued_as_new",
]);
export type RunStatus = z.infer<typeof RunStatusSchema>;

export const RunSummarySchema = z.object({
  workflowId: z.string(),
  runId: z.string(),
  workflowKey: z.string(),
  sourceId: z.string().nullable(),
  sourceName: z.string().nullable(),
  trigger: TriggerSchema,
  status: RunStatusSchema,
  startedAt: z.string().datetime(),
  closedAt: z.string().datetime().nullable(),
  progress: z
    .object({
      task: z.string(),
      taskIndex: z.number(),
      taskCount: z.number(),
      fetched: z.number(),
      message: z.string().optional(),
      emailFound: z.boolean().optional(),
    })
    .nullable(),
  /** Why a failed run failed. Null while it is running, or when it did not. */
  error: z.string().nullable().default(null),
});
export type RunSummary = z.infer<typeof RunSummarySchema>;

/** Connection and source inputs for the integrations surface. */
export const ConnectionInputSchema = z.object({
  provider: z.enum(["github", "reddit", "slack", "webhook", "anthropic"]),
  name: z.string().trim().min(1).max(100),
  account: z.string().trim().min(1).max(500),
  /** Write-only. Never returned by any read path. */
  token: z.string().min(1).max(4000).optional(),
  enabled: z.boolean().default(true),
});
export type ConnectionInput = z.infer<typeof ConnectionInputSchema>;

export const SourceInputSchema = z.object({
  integrationId: z.string().uuid(),
  /** Provider-specific: `owner/name` for GitHub, a subreddit for Reddit. */
  identifier: z.string().trim().min(1).max(200),
  /** Tracking makes a source available; only a run or schedule starts work. */
  enabled: z.boolean().default(true),
});
export type SourceInput = z.infer<typeof SourceInputSchema>;
